import { useEffect, useState } from 'react';
import { CHECKIN_DISPLAY_SECONDS } from '@sundkortid/shared';
import { api, ApiRequestError } from '../../api/client';
import { useAuthStore } from '../../stores/auth';

interface Pool {
  id: string;
  name: string;
  city: string;
}

interface Redemption {
  id: string;
  passId: string;
  scannedAt: string;
  isReversed: boolean;
  customerName: string;
  verificationCode: string | null;
  method?: 'pool_qr' | 'manual';
}

interface Banner {
  code: string;
  name: string;
  at: number;
}

const POOL_STORAGE_KEY = 'sundkortid_staff_pool';

function formatTime(dateStr: string) {
  return new Date(dateStr).toLocaleTimeString('is-IS', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function StaffDashboardPage() {
  const [pools, setPools] = useState<Pool[]>([]);
  const [selectedPoolId, setSelectedPoolId] = useState<string>(
    () => sessionStorage.getItem(POOL_STORAGE_KEY) ?? '',
  );
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [isLoadingPools, setIsLoadingPools] = useState(true);
  const [isLoadingRedemptions, setIsLoadingRedemptions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [newRedemptionId, setNewRedemptionId] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<Pool[]>('/pools/my-assignments')
      .then((data) => {
        setPools(data);
        if (data.length === 1 && !selectedPoolId) {
          setSelectedPoolId(data[0]!.id);
        }
      })
      .catch((err) =>
        setError(err instanceof ApiRequestError ? err.message : 'Villa við að sækja sundlaugar'),
      )
      .finally(() => setIsLoadingPools(false));
  }, []);

  useEffect(() => {
    if (!selectedPoolId) return;
    sessionStorage.setItem(POOL_STORAGE_KEY, selectedPoolId);

    setIsLoadingRedemptions(true);
    api
      .get<Redemption[]>(`/redemptions?poolId=${selectedPoolId}`)
      .then(setRedemptions)
      .catch(() => setRedemptions([]))
      .finally(() => setIsLoadingRedemptions(false));
  }, [selectedPoolId]);

  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), CHECKIN_DISPLAY_SECONDS * 1000);
    return () => clearTimeout(t);
  }, [banner]);

  useEffect(() => {
    if (!selectedPoolId) return;
    const token = useAuthStore.getState().accessToken;
    if (!token) return;

    const apiBase = import.meta.env.VITE_API_URL || '';
    const wsBase = apiBase.replace(/^http/, 'ws') || `ws://${location.host}`;
    const ws = new WebSocket(
      `${wsBase}/api/v1/ws/staff-notify?token=${encodeURIComponent(token)}&poolId=${selectedPoolId}`,
    );

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'staff_redemption' && data.redemption) {
          const newR: Redemption = {
            id: data.redemption.id,
            passId: data.redemption.passId,
            scannedAt: data.redemption.scannedAt,
            isReversed: data.redemption.isReversed,
            customerName: data.redemption.customerName,
            verificationCode: data.redemption.verificationCode ?? null,
            method: data.redemption.method,
          };
          setRedemptions((prev) => [newR, ...prev]);
          setNewRedemptionId(newR.id);
          setTimeout(() => setNewRedemptionId(null), 2000);
          if (newR.verificationCode) {
            setBanner({ code: newR.verificationCode, name: newR.customerName, at: Date.now() });
          }
        }
      } catch {
        // ignore parse errors
      }
    };

    return () => {
      ws.close();
    };
  }, [selectedPoolId]);

  const activeCount = redemptions.filter((r) => !r.isReversed).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-xl font-title font-bold text-primary">Sundkortið — Starfsfólk</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Sundlaug</label>
          {isLoadingPools ? (
            <div className="h-10 bg-gray-100 rounded-lg animate-pulse w-64" />
          ) : pools.length === 0 ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              Engin sundlaug hefur verið úthlutað á þig. Hafðu samband við stjórnanda.
            </p>
          ) : pools.length === 1 ? (
            <p className="text-sm font-medium text-gray-900">{pools[0]!.name}</p>
          ) : (
            <select
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white w-64"
              value={selectedPoolId}
              onChange={(e) => setSelectedPoolId(e.target.value)}
            >
              <option value="">Veldu sundlaug…</option>
              {pools.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3 mb-6">{error}</p>
        )}

        {banner && (
          <div
            key={banner.at}
            className="mb-8 rounded-2xl bg-gradient-to-br from-emerald-900 via-emerald-600 to-emerald-400 px-8 py-10 text-center shadow-xl animate-in fade-in"
          >
            <p className="text-xs font-bold tracking-[0.3em] text-emerald-50/80 mb-3">
              NÝ SKRÁNING
            </p>
            <p className="text-8xl font-extrabold text-white leading-none tracking-tight mb-4">
              {banner.code}
            </p>
            <p className="text-2xl font-semibold text-white/95">{banner.name}</p>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Notkun í dag{' '}
            {activeCount > 0 && (
              <span className="ml-2 text-sm font-normal bg-primary text-white px-2 py-0.5 rounded-full">
                {activeCount}
              </span>
            )}
          </h3>
          {selectedPoolId && (
            <button
              className="text-sm text-primary hover:underline"
              onClick={() => {
                setIsLoadingRedemptions(true);
                api
                  .get<Redemption[]>(`/redemptions?poolId=${selectedPoolId}`)
                  .then(setRedemptions)
                  .catch(() => {})
                  .finally(() => setIsLoadingRedemptions(false));
              }}
            >
              Uppfæra
            </button>
          )}
        </div>

        {!selectedPoolId && (
          <p className="text-gray-500 text-sm">Veldu sundlaug til að sjá notkun.</p>
        )}

        {selectedPoolId && isLoadingRedemptions && (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-gray-100 p-3 h-12 animate-pulse"
              />
            ))}
          </div>
        )}

        {selectedPoolId && !isLoadingRedemptions && redemptions.length === 0 && (
          <p className="text-gray-500">Engin notkun skráð í dag.</p>
        )}

        {selectedPoolId && !isLoadingRedemptions && redemptions.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Kóði</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Nafn</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Tími</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Staða</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {redemptions.map((r) => (
                  <tr
                    key={r.id}
                    className={`${r.isReversed ? 'opacity-50' : ''} ${r.id === newRedemptionId ? 'bg-green-50 transition-colors duration-2000' : ''}`}
                  >
                    <td className="px-4 py-3 font-mono font-bold text-primary-900 text-base">
                      {r.verificationCode ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-900">{r.customerName}</td>
                    <td className="px-4 py-3 text-gray-600">{formatTime(r.scannedAt)}</td>
                    <td className="px-4 py-3">
                      {r.isReversed ? (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                          Afturkallað
                        </span>
                      ) : (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          Gilt
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
